import { apm } from "@elastic/apm-rum";
import {
  Close,
  EmojiPeople,
  LogoutOutlined,
  ManageAccountsOutlined,
  NewReleasesOutlined,
  ToggleOnOutlined,
} from "@mui/icons-material";
import { Box, Divider, Menu, MenuItem, Typography } from "@mui/material";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import UseAuth from "shared/common/hooks/UseAuth";
import { monitor } from "shared/common/services/apm-logging";
import { logout } from "shared/common/services/Login";
import { defaultStore } from "shared/common/stores/defaultStore";
import MenuWithPointerForwarding from "shared/ui/muiComponentWrappers/MenuWithPointerForwarding";
import FeedbackForm from "./FeedbackForm";
import AuthContext from "../../../../common/contexts/AuthProvider";
import ClientConfig from "../../../../config";

interface props {
  anchorEl: HTMLElement | null;
  UserId: string;
  handleUserClose: () => void;
  redirectToClassic: () => string;
  role?: string;
}

const UserMenu = ({
  anchorEl,
  UserId,
  handleUserClose,
  role,
  redirectToClassic,
}: props) => {
  //All about Language (i18n)
  const { t } = useTranslation();
  const { userData, logoutAPI } = UseAuth();
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const [logoutState, setLogout] = useState<boolean>(false);
  const [changeToClassic, setChangeToClassic] = useState<boolean>(false);

  const isUserOpen = Boolean(anchorEl);

  const darkTheme = localStorage.getItem("theme") === "dark";

  //redirect to platform link
  const redirectToPlatform = () => {
    window.location.href = "/";
  };

  //close modal 'change to classic'
  const handleCloseForm = () => {
    setChangeToClassic(false);
  };

  const handleProfile = () => {
    handleUserClose();
    let redirect_uri = "";
    if (ClientConfig.stage === "prod") {
      redirect_uri = "https://app.infra3d.com/profile";
    } else {
      redirect_uri = `https://app.${ClientConfig.stage}.infra3d.com/profile`;
    }
    window.open(redirect_uri, "_blank");
  };

  return (
    <MenuWithPointerForwarding
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      id={UserId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        padding: 0,
        marginTop: "-6px",
      }}
      open={isUserOpen}
      onClose={handleUserClose}
      classes={{
        list: darkTheme ? "infra3d-menuListDark" : "infra3d-menuListLight",
      }}
    >
      {userData !== undefined &&
        (userData.user_firstname ||
          userData.user_familyname ||
          userData.usermail) && (
          <MenuItem
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
            }}
          >
            {(userData.user_firstname || userData.user_familyname) && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                }}
              >
                {userData.user_firstname && userData.user_firstname}{" "}
                {userData.user_familyname && userData.user_familyname}
              </Typography>
            )}
            {userData.usermail && (
              <Typography variant="caption">{userData.usermail}</Typography>
            )}
          </MenuItem>
        )}
      {/* Divider needs to be in a second if statement, 
      as an empty <></> element is not allowed in a menu... */}
      {userData !== undefined &&
        (userData.user_firstname ||
          userData.user_familyname ||
          userData.usermail) && (
          <Divider
            sx={{
              backgroundColor: darkTheme ? "white" : "darkblue.main",
              marginBottom: "8px",
            }}
          />
        )}
      <MenuItem
        sx={{
          gap: "10px",
          whiteSpace: "normal",
          color: darkTheme ? "white" : "darkblue.main",
        }}
        disabled={userData?.isMigrated === false || role === "api"}
        onClick={() =>
          monitor(apm, "click-on-profile | Settings", handleProfile)
        }
      >
        <ManageAccountsOutlined />
        <Typography variant="body1">{t("usermenu.profile")}</Typography>
      </MenuItem>
      {role !== "api" && (
        <MenuItem
          sx={{
            gap: "10px",
            whiteSpace: "normal",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          onClick={() =>
            monitor(apm, "close-project | Settings", () => {
              redirectToPlatform();
            })
          }
        >
          <Close />
          <Typography variant="body1">
            {t("usermenu.back_to_platform")}
          </Typography>
        </MenuItem>
      )}
      {role !== "api" && userData?.usergroups[0] !== "guest" && (
        <MenuItem
          sx={{
            gap: "10px",
            whiteSpace: "normal",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          onClick={() =>
            monitor(apm, "change-to-classic | Settings", () => {
              setChangeToClassic(true);
            })
          }
          className="onboarding-platform"
        >
          <ToggleOnOutlined />
          <Typography variant="body1">
            {t("usermenu.back_to_classic")}
          </Typography>
        </MenuItem>
      )}
      <Box
        sx={{
          width: "200%",
          display: "flex",
          overflow: "hidden",
        }}
      >
        <MenuItem
          sx={{
            gap: "10px",
            width: "50%",
            left: logoutState ? "-50%" : "0%",
            whiteSpace: "normal",
            transition: "left 0.3s",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          onClick={() =>
            monitor(apm, "logout | Settings", () => {
              setLogout(true);
              const logoutTime = setTimeout(() => {
                if (role === "api") {
                  logoutAPI();
                } else {
                  logout();
                }
              }, 1000);
              return () => clearTimeout(logoutTime);
            })
          }
        >
          <LogoutOutlined />
          <Typography variant="body1">{t("usermenu.logout")}</Typography>
        </MenuItem>
        <MenuItem
          sx={{
            gap: "10px",
            width: "50%",
            left: logoutState ? "-50%" : "0%",
            whiteSpace: "normal",
            transition: "left 0.3s",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
        >
          <EmojiPeople />
          {userData !== undefined && (
            <Typography variant="body1">
              {userData?.user_firstname
                ? `${t("usermenu.logout_message")}, ${userData.user_firstname}!`
                : t("usermenu.logout_message")}
            </Typography>
          )}
        </MenuItem>
      </Box>
      {changeToClassic && (
        <FeedbackForm
          showFeedback={changeToClassic}
          handleClose={handleCloseForm}
          redirectToClassic={redirectToClassic}
        />
      )}
    </MenuWithPointerForwarding>
  );
};

export default UserMenu;
